import { CreateFullPage } from '@carbon/ibm-products';
import { Loading, Modal } from '@carbon/react';
import {
    AddressElement,
    CardElement,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import sharedMessages from 'Components/utils/sharedMessages';
import {
    emptyInvoiceDetailsForm,
    composeInvoiceStreet,
    hasCompleteRequiredInvoiceDetails,
    normalizeInvoiceDetailsFormValues,
    toInvoiceDetailsUpsertDto,
} from 'Components/Common/InvoiceDetailsForm/InvoiceDetailsForm';
import type { InvoiceDetailsFormValues } from 'Components/Common/InvoiceDetailsForm/InvoiceDetailsForm';
import {
    emptyAddressFormValues,
    hasCompleteRequiredAddress,
    normalizeAddressFormValues,
    toAddressDto,
} from 'Components/Common/AddressForm/AddressForm';
import type { AddressFormValues } from 'Components/Common/AddressForm/AddressForm';
import { useInitialSetupMutation } from 'api/accountEndpoints';
import { useGetActivePlansQuery } from 'api/subscriptionPlanEndpoints';
import useGetUser from 'hooks/useGetUser';
import { useStorage } from 'hooks/useStorage';
import {
    CONTACT_ROLE_MAX_LENGTH,
    CONTACT_PERSON_DESCRIPTION_MAX_LENGTH,
    CONTACT_PERSON_TEXT_MAX_LENGTH,
    DEFAULT_COUNTRY_PHONE_PREFIX,
    isEmailValid,
    isPhoneNumberValid,
    sanitizePhoneNumber,
} from 'utils/validationUtils';

import ExitSetupModal from './components/ExitSetupModal/ExitSetupModal';
import { INITIAL_SETUP_DRAFT_STORAGE_KEY } from './constants';
import { createDefaultCategories, createDefaultPersonFormState, createLocalId } from './helpers';
import messages from './messages';
import styles from './InitialSetupModal.module.scss';
import GroupStep from './steps/GroupStep/GroupStep';
import PeopleStep from './steps/PeopleStep/PeopleStep';
import SubscriptionStep from './steps/SubscriptionStep/SubscriptionStep';
import SummaryStep from './steps/SummaryStep/SummaryStep';
import { InitialSetupDraft, LocalCategory, LocalPerson, PersonFormState } from './types';

const invoiceDetailsRequiredMessage: MessageDescriptor = {
    id: 'src.Screens.Dashboard.InitialSetup.InitialSetupModal.invoiceDetailsRequired',
    defaultMessage: 'Uzupełnij wszystkie wymagane dane do faktury albo wyczyść tę sekcję.',
};

const accountAddressRequiredMessage: MessageDescriptor = {
    id: 'src.Screens.Dashboard.InitialSetup.InitialSetupModal.accountAddressRequired',
    defaultMessage: 'Uzupełnij wymagane dane osoby/konta.',
};

type Props = {
    open: boolean;
    onClose: () => void;
};

const InitialSetupModal = ({ open, onClose }: Props) => {
    const intl = useIntl();
    const user = useGetUser();
    const stripe = useStripe();
    const elements = useElements();

    const { specifiedGetItem, specifiedSetItem, specifiedRemoveItem } = useStorage('local', user?.id ?? '');

    const [initialSetup, { isLoading }] = useInitialSetupMutation();
    const { data: plans = [], isFetching: isFetchingPlans } = useGetActivePlansQuery();

    const [setupError, setSetupError] = useState('');
    const [exitModalOpen, setExitModalOpen] = useState(false);
    const [isClosingConfiguration, setIsClosingConfiguration] = useState(false);

    const [entityName, setEntityName] = useState('');
    const [entityEmail, setEntityEmail] = useState('');
    const [entityPhoneNumber, setEntityPhoneNumber] = useState(DEFAULT_COUNTRY_PHONE_PREFIX);
    const [entityWebsite, setEntityWebsite] = useState('');

    const [categories, setCategories] = useState<LocalCategory[]>(createDefaultCategories());

    const [personForm, setPersonForm] = useState<PersonFormState>(createDefaultPersonFormState());
    const [people, setPeople] = useState<LocalPerson[]>([]);

    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [address, setAddress] = useState<AddressFormValues>(emptyAddressFormValues);
    const [invoiceDetailsSameAsAddress, setInvoiceDetailsSameAsAddress] = useState(true);
    const [invoiceDetails, setInvoiceDetails] = useState<InvoiceDetailsFormValues>(emptyInvoiceDetailsForm);
    const [showAddressValidationErrors, setShowAddressValidationErrors] = useState(false);
    const [showInvoiceValidationErrors, setShowInvoiceValidationErrors] = useState(false);

    const [setAsDefaultPaymentMethod, setSetAsDefaultPaymentMethod] = useState(true);
    const [isCardComplete, setIsCardComplete] = useState(false);
    const [isBillingAddressComplete, setIsBillingAddressComplete] = useState(false);
    const skipNextCreateFullPageClosePrompt = useRef(false);

    const closeConfiguration = () => {
        skipNextCreateFullPageClosePrompt.current = true;
        setExitModalOpen(false);
        onClose();
    };

    useEffect(() => {
        if (!open || !user) {
            return;
        }

        const draft = specifiedGetItem<InitialSetupDraft | null>(INITIAL_SETUP_DRAFT_STORAGE_KEY, null);
        if (!draft) {
            setEntityName('');
            setEntityEmail('');
            setEntityPhoneNumber(DEFAULT_COUNTRY_PHONE_PREFIX);
            setEntityWebsite('');
            setCategories(createDefaultCategories());
            setPeople([]);
            setSelectedPlanId('');
            setAddress(emptyAddressFormValues);
            setInvoiceDetailsSameAsAddress(true);
            setInvoiceDetails(emptyInvoiceDetailsForm);
            setSetAsDefaultPaymentMethod(true);
            setShowAddressValidationErrors(false);
            setShowInvoiceValidationErrors(false);
            return;
        }

        setEntityName(draft.entityName);
        setEntityEmail(draft.entityEmail);
        setEntityPhoneNumber(draft.entityPhoneNumber || DEFAULT_COUNTRY_PHONE_PREFIX);
        setEntityWebsite(draft.entityWebsite);
        setCategories(draft.categories.length > 0 ? draft.categories : createDefaultCategories());
        setPeople(draft.people);
        setSelectedPlanId(draft.selectedPlanId);
        setAddress(normalizeAddressFormValues(draft.address));
        setInvoiceDetailsSameAsAddress(draft.invoiceDetailsSameAsAddress ?? true);
        setInvoiceDetails(normalizeInvoiceDetailsFormValues(draft.invoiceDetails));
        setSetAsDefaultPaymentMethod(draft.setAsDefaultPaymentMethod);
        setShowAddressValidationErrors(false);
        setShowInvoiceValidationErrors(false);
    }, [open, specifiedGetItem, user]);

    const selectedPlan = useMemo(
        () => plans.find((plan) => plan.id === selectedPlanId) ?? null,
        [plans, selectedPlanId],
    );

    const isEntityEmailValid = useMemo(
        () => isEmailValid(entityEmail),
        [entityEmail],
    );

    const isEntityPhoneNumberValid = useMemo(
        () => entityPhoneNumber === DEFAULT_COUNTRY_PHONE_PREFIX || isPhoneNumberValid(entityPhoneNumber),
        [entityPhoneNumber],
    );

    const trimmedCategoryNames = useMemo(
        () => categories.map((category) => category.name.trim().toLocaleLowerCase()),
        [categories],
    );

    const hasEmptyCategory = useMemo(
        () => categories.some((category) => category.name.trim().length < 3),
        [categories],
    );

    const isPersonFormEmailValid = useMemo(
        () => isEmailValid(personForm.email),
        [personForm.email],
    );

    const isPersonFormPhoneValid = useMemo(
        () => isPhoneNumberValid(personForm.phoneNumber),
        [personForm.phoneNumber],
    );

    const isPersonDraftValid = useMemo(() => (
        personForm.firstName.trim().length > 0
        && personForm.firstName.trim().length <= CONTACT_PERSON_TEXT_MAX_LENGTH
        && personForm.lastName.trim().length > 0
        && personForm.lastName.trim().length <= CONTACT_PERSON_TEXT_MAX_LENGTH
        && personForm.role.trim().length > 0
        && personForm.role.trim().length <= CONTACT_ROLE_MAX_LENGTH
        && personForm.availability.trim().length <= CONTACT_PERSON_TEXT_MAX_LENGTH
        && personForm.description.trim().length <= CONTACT_PERSON_DESCRIPTION_MAX_LENGTH
        && personForm.categoryId.length > 0
        && isPersonFormEmailValid
        && isPersonFormPhoneValid
    ), [isPersonFormEmailValid, isPersonFormPhoneValid, personForm]);

    const isGroupStepDisabled = entityName.trim().length === 0 || !isEntityEmailValid || !isEntityPhoneNumberValid;
    const hasDuplicateCategory = useMemo(
        () => new Set(trimmedCategoryNames).size !== trimmedCategoryNames.length,
        [trimmedCategoryNames],
    );

    const isCategoriesStepDisabled = hasDuplicateCategory || hasEmptyCategory;
    const isPeopleStepDisabled = isCategoriesStepDisabled;
    const isAddressComplete = hasCompleteRequiredAddress(address);
    const isInvoiceDetailsComplete = invoiceDetailsSameAsAddress || hasCompleteRequiredInvoiceDetails(invoiceDetails);
    const isSubscriptionStepDisabled = selectedPlanId.length === 0
        || !isCardComplete
        || !isBillingAddressComplete
        || !isAddressComplete
        || !isInvoiceDetailsComplete;

    const persistDraft = () => {
        if (!user) {
            return;
        }

        specifiedSetItem(INITIAL_SETUP_DRAFT_STORAGE_KEY, {
            entityName,
            entityEmail,
            entityPhoneNumber,
            entityWebsite,
            categories,
            people,
            selectedPlanId,
            setAsDefaultPaymentMethod,
            address,
            invoiceDetailsSameAsAddress,
            invoiceDetails,
        } satisfies InitialSetupDraft);
    };

    const handleExitAndSave = () => {
        setIsClosingConfiguration(true);

        try {
            persistDraft();
            closeConfiguration();
        } catch {
            setSetupError(intl.formatMessage(messages.submitFailed));
        } finally {
            setIsClosingConfiguration(false);
        }
    };

    const handleExitWithoutSave = () => {
        setIsClosingConfiguration(true);

        try {
            closeConfiguration();
        } catch {
            setSetupError(intl.formatMessage(messages.submitFailed));
        } finally {
            setIsClosingConfiguration(false);
        }
    };

    const handleAddCategory = (name: string) => {
        const normalized = name.trim();
        if (normalized.length < 3) {
            return;
        }

        if (normalized.length > CONTACT_ROLE_MAX_LENGTH) {
            return;
        }

        if (trimmedCategoryNames.includes(normalized.toLocaleLowerCase())) {
            return;
        }

        setCategories((previous) => [...previous, { id: createLocalId(), name: normalized }]);
    };

    const handleRemoveCategory = (categoryId: string) => {
        setCategories((previous) => previous.filter((category) => category.id !== categoryId));
        setPeople((previous) => previous.filter((person) => person.categoryId !== categoryId));

        if (personForm.categoryId === categoryId) {
            setPersonForm((previous) => ({ ...previous, categoryId: '' }));
        }
    };

    const handleAddPerson = () => {
        if (!isPersonDraftValid) {
            return;
        }

        setPeople((previous) => ([
            ...previous,
            {
                id: createLocalId(),
                categoryId: personForm.categoryId,
                firstName: personForm.firstName.trim(),
                lastName: personForm.lastName.trim(),
                role: personForm.role.trim(),
                availability: personForm.availability.trim(),
                description: personForm.description.trim(),
                phoneNumber: personForm.phoneNumber.trim(),
                email: personForm.email.trim(),
                visibleForGuests: personForm.visibleForGuests,
            },
        ]));

        setPersonForm(createDefaultPersonFormState());
    };

    const handleRemovePerson = (personId: string) => {
        setPeople((previous) => previous.filter((person) => person.id !== personId));
    };

    const handleInvoiceDetailsChange = (field: keyof InvoiceDetailsFormValues, value: string) => {
        setShowInvoiceValidationErrors(false);
        setInvoiceDetails((previous) => ({
            ...previous,
            [field]: value,
        }));
    };

    const handleAddressChange = (field: keyof AddressFormValues, value: string) => {
        setShowAddressValidationErrors(false);
        setAddress((previous) => ({
            ...previous,
            [field]: value,
        }));
    };

    const handleSubmit = async () => {
        if (!stripe || !elements || !user || !selectedPlan) {
            setSetupError(intl.formatMessage(messages.submitMissingData));
            throw new Error('Initial setup submit missing data');
        }

        if (!hasCompleteRequiredAddress(address)) {
            setShowAddressValidationErrors(true);
            setSetupError(intl.formatMessage(accountAddressRequiredMessage));
            throw new Error('Initial setup account address incomplete');
        }

        if (!invoiceDetailsSameAsAddress && !hasCompleteRequiredInvoiceDetails(invoiceDetails)) {
            setShowInvoiceValidationErrors(true);
            setSetupError(intl.formatMessage(invoiceDetailsRequiredMessage));
            throw new Error('Initial setup invoice details incomplete');
        }

        setSetupError('');

        const cardElement = elements.getElement(CardElement);
        const addressElement = elements.getElement(AddressElement);

        if (!cardElement || !addressElement) {
            setSetupError(intl.formatMessage(messages.paymentElementMissing));
            throw new Error('Initial setup payment element missing');
        }

        const addressResult = await addressElement.getValue();
        if (!addressResult.complete) {
            setSetupError(intl.formatMessage(messages.billingAddressRequired));
            throw new Error('Initial setup billing address incomplete');
        }

        const paymentMethodResult = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
            billing_details: {
                name: addressResult.value.name,
                email: user.email,
                address: {
                    line1: addressResult.value.address.line1,
                    line2: addressResult.value.address.line2,
                    city: addressResult.value.address.city,
                    state: addressResult.value.address.state,
                    postal_code: addressResult.value.address.postal_code,
                    country: addressResult.value.address.country,
                },
            },
        });

        const { paymentMethod } = paymentMethodResult;
        if (!paymentMethod || !paymentMethod.card) {
            setSetupError(intl.formatMessage(messages.paymentFailed));
            throw new Error('Initial setup payment method creation failed');
        }

        try {
            await initialSetup({
                paymentDetails: {
                    paymentToken: paymentMethod.id,
                    paymentMethodType: paymentMethod.type,
                    lastFourDigits: paymentMethod.card.last4,
                    cardBrand: paymentMethod.card.brand,
                    expirationMonth: paymentMethod.card.exp_month,
                    expirationYear: String(paymentMethod.card.exp_year),
                    billingAddress: {
                        fullName: addressResult.value.name,
                        line1: addressResult.value.address.line1,
                        line2: addressResult.value.address.line2,
                        city: addressResult.value.address.city,
                        state: addressResult.value.address.state,
                        postalCode: addressResult.value.address.postal_code,
                        country: addressResult.value.address.country,
                        email: user.email,
                    },
                    isDefault: setAsDefaultPaymentMethod,
                },
                address: toAddressDto(address),
                invoiceDetails: invoiceDetailsSameAsAddress
                    ? undefined
                    : toInvoiceDetailsUpsertDto(invoiceDetails),
                subscriptionPlanId: selectedPlan.id,
                entity: {
                    name: entityName.trim(),
                    email: entityEmail.trim(),
                    phoneNumber: entityPhoneNumber.trim() === DEFAULT_COUNTRY_PHONE_PREFIX
                        ? ''
                        : entityPhoneNumber.trim(),
                    website: entityWebsite.trim(),
                    isEnabled: true,
                    tags: [],
                    isIndependent: false,
                    parentId: null,
                    usesPoints: true,
                },
                parishBookCategories: categories.map((category) => ({
                    name: category.name.trim(),
                    persons: people
                        .filter((person) => person.categoryId === category.id)
                        .map((person) => ({
                            firstName: person.firstName,
                            lastName: person.lastName,
                            role: person.role,
                            availability: person.availability || undefined,
                            description: person.description || undefined,
                            phoneNumber: person.phoneNumber || undefined,
                            email: person.email || undefined,
                            visibleForGuests: person.visibleForGuests,
                        })),
                })),
            }).unwrap();

            specifiedRemoveItem(INITIAL_SETUP_DRAFT_STORAGE_KEY);
            closeConfiguration();
        } catch {
            setSetupError(intl.formatMessage(messages.submitFailed));
            throw new Error('Initial setup request failed');
        }
    };

    const getCategoryName = (categoryId: string) => categories.find((category) => category.id === categoryId)?.name ?? '-';
    const summaryNotProvided = intl.formatMessage(messages.summaryNotProvided);
    const getSummaryValue = (value: string) => value.trim() || summaryNotProvided;
    const accountCityLine = [address.postalCode, address.city]
        .map((value) => value.trim())
        .filter(Boolean)
        .join(' ');
    const accountAddressSummary = [
        composeInvoiceStreet(address),
        accountCityLine,
        address.country,
    ]
        .map((value) => value.trim())
        .filter(Boolean)
        .join(', ') || summaryNotProvided;
    const invoiceCityLine = [invoiceDetails.postalCode, invoiceDetails.city]
        .map((value) => value.trim())
        .filter(Boolean)
        .join(' ');
    const invoiceAddressSummary = [
        composeInvoiceStreet(invoiceDetails),
        invoiceCityLine,
        invoiceDetails.voivodeship,
        invoiceDetails.country,
    ]
        .map((value) => value.trim())
        .filter(Boolean)
        .join(', ') || summaryNotProvided;

    return (
        <div className={styles.wrapper}>
            {(isLoading || isFetchingPlans || isClosingConfiguration) && <Loading />}
            <Modal
                preventCloseOnClickOutside
                open={open}
                passiveModal
                className={styles.modal}
                size="lg"
            >
                {open && (
                    <CreateFullPage
                        className={styles.fullPage}
                        title={intl.formatMessage(messages.title)}
                        backButtonText={intl.formatMessage(sharedMessages.back)}
                        cancelButtonText={intl.formatMessage(messages.cancelSetup)}
                        nextButtonText={intl.formatMessage(sharedMessages.next)}
                        submitButtonText={intl.formatMessage(messages.finishSetup)}
                        modalTitle={intl.formatMessage(messages.closeModalTitle)}
                        modalDescription={intl.formatMessage(messages.closeModalDescription)}
                        modalSecondaryButtonText={intl.formatMessage(sharedMessages.back)}
                        modalDangerButtonText={intl.formatMessage(messages.closeSetup)}
                        onClose={() => {
                            if (skipNextCreateFullPageClosePrompt.current) {
                                skipNextCreateFullPageClosePrompt.current = false;
                                return;
                            }

                            setExitModalOpen(true);
                        }}
                        // @ts-expect-error CreateFullPage typing does not include async return in current package typings
                        onRequestSubmit={handleSubmit}
                    >
                        <GroupStep
                            title={messages.groupStepTitle}
                            groupNameLabel={messages.parishName}
                            websiteLabel={messages.parishWebsite}
                            entityName={entityName}
                            entityEmail={entityEmail}
                            entityPhoneNumber={entityPhoneNumber}
                            entityWebsite={entityWebsite}
                            isEntityEmailValid={isEntityEmailValid}
                            isEntityPhoneNumberValid={isEntityPhoneNumberValid}
                            isDisabled={isGroupStepDisabled}
                            onEntityNameChange={setEntityName}
                            onEntityEmailChange={setEntityEmail}
                            onEntityPhoneNumberChange={(value) => { setEntityPhoneNumber(sanitizePhoneNumber(value)); }}
                            onEntityWebsiteChange={setEntityWebsite}
                        />

                        <PeopleStep
                            title={messages.combinedPeopleStepTitle}
                            categoriesTitle={messages.categoriesStepTitle}
                            categoriesDescription={messages.categoriesDescription}
                            labels={{
                                addPeopleTitle: messages.addPeopleTitle,
                                currentPeopleTitle: messages.currentPeopleTitle,
                                personCategory: messages.personCategory,
                                selectCategory: messages.selectCategory,
                                firstName: messages.firstName,
                                lastName: messages.lastName,
                                role: messages.role,
                                availability: messages.availability,
                                description: messages.description,
                                visibleForGuests: messages.visibleForGuests,
                            }}
                            categories={categories}
                            people={people}
                            personForm={personForm}
                            isPersonFormEmailValid={isPersonFormEmailValid}
                            isPersonFormPhoneValid={isPersonFormPhoneValid}
                            isPersonDraftValid={isPersonDraftValid}
                            isDisabled={isPeopleStepDisabled}
                            setPersonForm={setPersonForm}
                            onAddCategory={handleAddCategory}
                            onRemoveCategory={handleRemoveCategory}
                            onAddPerson={handleAddPerson}
                            onRemovePerson={handleRemovePerson}
                            getCategoryName={getCategoryName}
                        />

                        <SubscriptionStep
                            title={messages.subscriptionStepTitle}
                            description={messages.subscriptionDescription}
                            monthLabel={messages.month}
                            choosePlanLabel={messages.choosePlan}
                            selectedPlanLabel={messages.selectedPlan}
                            paymentTitle={messages.paymentStepTitle}
                            paymentDescription={messages.paymentDescription}
                            accountAddressTitle={messages.accountAddressTitle}
                            accountAddressDescription={messages.accountAddressDescription}
                            invoiceDetailsTitle={messages.invoiceDetailsTitle}
                            invoiceDetailsDescription={messages.invoiceDetailsDescription}
                            plans={plans}
                            selectedPlanId={selectedPlanId}
                            isDisabled={isSubscriptionStepDisabled}
                            setAsDefaultPaymentMethod={setAsDefaultPaymentMethod}
                            address={address}
                            invoiceDetailsSameAsAddress={invoiceDetailsSameAsAddress}
                            onSelectPlan={setSelectedPlanId}
                            onSetCardComplete={setIsCardComplete}
                            onSetBillingAddressComplete={setIsBillingAddressComplete}
                            onSetAsDefaultPaymentMethod={setSetAsDefaultPaymentMethod}
                            onAddressChange={handleAddressChange}
                            onInvoiceDetailsSameAsAddressChange={(checked) => {
                                setInvoiceDetailsSameAsAddress(checked);
                                setShowInvoiceValidationErrors(false);
                            }}
                            invoiceDetails={invoiceDetails}
                            showAddressValidationErrors={showAddressValidationErrors}
                            showInvoiceValidationErrors={showInvoiceValidationErrors}
                            onInvoiceDetailsChange={handleInvoiceDetailsChange}
                        />

                        <SummaryStep
                            title={messages.summaryStepTitle}
                            description={messages.summaryDescription}
                            submitFailedTitle={messages.submitFailedTitle}
                            setupError={setupError}
                            summaryData={{
                                groupLabel: messages.groupStepTitle,
                                groupRows: [
                                    {
                                        key: 'group-name',
                                        label: intl.formatMessage(messages.summaryGroupName),
                                        value: entityName || intl.formatMessage(messages.summaryNotProvided),
                                    },
                                    {
                                        key: 'group-email',
                                        label: intl.formatMessage(messages.summaryGroupEmail),
                                        value: entityEmail || intl.formatMessage(messages.summaryNotProvided),
                                    },
                                ],
                                categoriesLabel: messages.categoriesStepTitle,
                                categoriesRows: [
                                    {
                                        key: 'categories-list',
                                        label: intl.formatMessage(messages.summaryCategoriesList),
                                        value: categories.length > 0
                                            ? categories.map((category) => category.name).join(', ')
                                            : intl.formatMessage(messages.summaryNotProvided),
                                    },
                                ],
                                peopleLabel: messages.peopleStepTitle,
                                peopleRows: [
                                    {
                                        key: 'people-count',
                                        label: intl.formatMessage(messages.summaryPeopleCount),
                                        value: String(people.length),
                                    },
                                ],
                                subscriptionLabel: messages.subscriptionStepTitle,
                                subscriptionRows: [
                                    {
                                        key: 'subscription-plan',
                                        label: intl.formatMessage(messages.summarySubscriptionPlan),
                                        value: selectedPlan?.name ?? intl.formatMessage(messages.summaryNotProvided),
                                    },
                                ],
                                paymentLabel: messages.paymentStepTitle,
                                paymentRows: [
                                    {
                                        key: 'payment-hidden',
                                        label: intl.formatMessage(messages.summaryCardData),
                                        value: intl.formatMessage(messages.cardHiddenSummary),
                                    },
                                ],
                                invoiceDetailsLabel: messages.invoiceDetailsTitle,
                                invoiceDetailsRows: [
                                    {
                                        key: 'account-address',
                                        label: intl.formatMessage(messages.summaryAccountAddress),
                                        value: accountAddressSummary,
                                    },
                                    {
                                        key: 'invoice-full-name',
                                        label: intl.formatMessage(messages.summaryInvoiceFullName),
                                        value: invoiceDetailsSameAsAddress
                                            ? 'Takie same jak dane osoby/konta'
                                            : getSummaryValue(invoiceDetails.fullName),
                                    },
                                    {
                                        key: 'invoice-email',
                                        label: intl.formatMessage(messages.summaryInvoiceEmail),
                                        value: invoiceDetailsSameAsAddress
                                            ? 'Takie same jak dane osoby/konta'
                                            : getSummaryValue(invoiceDetails.invoiceEmail),
                                    },
                                    {
                                        key: 'invoice-address',
                                        label: intl.formatMessage(messages.summaryInvoiceAddress),
                                        value: invoiceDetailsSameAsAddress ? accountAddressSummary : invoiceAddressSummary,
                                    },
                                    {
                                        key: 'invoice-company-name',
                                        label: intl.formatMessage(messages.summaryInvoiceCompanyName),
                                        value: getSummaryValue(invoiceDetails.companyName),
                                    },
                                    {
                                        key: 'invoice-tax-id',
                                        label: intl.formatMessage(messages.summaryInvoiceTaxId),
                                        value: getSummaryValue(invoiceDetails.taxId),
                                    },
                                    {
                                        key: 'invoice-eu-vat-id',
                                        label: intl.formatMessage(messages.summaryInvoiceEuVatId),
                                        value: getSummaryValue(invoiceDetails.euVatId),
                                    },
                                ],
                            }}
                        />
                    </CreateFullPage>
                )}
            </Modal>

            <ExitSetupModal
                open={exitModalOpen}
                isLoading={isClosingConfiguration}
                heading={messages.exitModalHeading}
                label={messages.closeSetup}
                description={messages.exitModalDescription}
                primaryButtonText={messages.saveAndContinueLater}
                secondaryButtonText={messages.exitWithoutSave}
                onClose={() => { setExitModalOpen(false); }}
                onPrimaryAction={handleExitAndSave}
                onSecondaryAction={handleExitWithoutSave}
            />
        </div>
    );
};

export default InitialSetupModal;
